import { useEffect, useImperativeHandle } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

// --- Validation -------------------------------------------------------

const numericSchema = z
  .string()
  .optional()
  .refine((val) => {
    if (!val || val === "") return true;
    return !isNaN(Number(val));
  }, "Must be a valid number")
  .transform((val) => (val && val !== "" ? Number(val) : val))
  .refine((val) => {
    if (val === null || val === undefined || val === "") return true;
    return val >= 0;
  }, "Value must be positive");

export const dataBoundsSchema = z.object({
  glucose_low: numericSchema,
  glucose_high: numericSchema,
  systolic_low: numericSchema,
  systolic_high: numericSchema,
  diastolic_low: numericSchema,
  diastolic_high: numericSchema,
  heart_rate_low: numericSchema,
  heart_rate_high: numericSchema,
  temperature_low: numericSchema,
  temperature_high: numericSchema,
  weight_low: numericSchema,
  weight_high: numericSchema,
  oxygen_low: numericSchema,
  oxygen_high: numericSchema,
});

export const defaultDataBoundsValues = {
  glucose_low: "",
  glucose_high: "",
  systolic_low: "",
  systolic_high: "",
  diastolic_low: "",
  diastolic_high: "",
  heart_rate_low: "",
  heart_rate_high: "",
  temperature_low: "",
  temperature_high: "",
  weight_low: "",
  weight_high: "",
  oxygen_low: "",
  oxygen_high: "",
};

// Field metadata shared by any UI that renders the bounds form.
// Order here matches the Figma design (Systolic -> ... -> Temperature).
export const DATA_BOUNDS_FIELDS = [
  { key: "systolic", label: "Systolic", unit: "mmHg" },
  { key: "diastolic", label: "Diastolic", unit: "mmHg" },
  { key: "heart_rate", label: "Heart Rate", unit: "bpm" },
  { key: "glucose", label: "Glucose", unit: "mg/dL" },
  { key: "weight", label: "Weight", unit: "lb" },
  { key: "oxygen", label: "Oxygen", unit: "%" },
  { key: "temperature", label: "Temperature", unit: "°F" },
];

/**
 * Shared data-bounds form logic, extracted from DataBounds.js so it can be
 * reused by any UI (patient-settings accordion, org-level modal, etc.)
 * without duplicating the react-hook-form / zod setup.
 *
 * Usage:
 *   const { register, formState: { errors } } = useDataBoundsForm(data_bounds, ref);
 *
 * Exposes the same imperative API as the original DataBounds component:
 *   ref.current.getValidatedData() -> Promise<convertedValues | null>
 */
export const useDataBoundsForm = (data_bounds, ref) => {
  const form = useForm({
    resolver: zodResolver(dataBoundsSchema),
    defaultValues: defaultDataBoundsValues,
    mode: "onChange",
  });

  const { setValue, getValues, trigger, formState } = form;

  useImperativeHandle(ref, () => ({
    getValidatedData: async () => {
      const isValid = await trigger();
      if (!isValid) return null;

      const formValues = getValues();
      const convertedValues = {};

      Object.entries(formValues).forEach(([key, value]) => {
        convertedValues[key] =
          value !== "" && value !== null && value !== undefined
            ? Number(value)
            : 0;
      });

      return convertedValues;
    },
    isDirty: formState.isDirty,
  }));

  useEffect(() => {
    if (data_bounds) {
      Object.entries(data_bounds).forEach(([key, value]) => {
        setValue(key, value?.toString() ?? "");
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data_bounds]);

  return form;
};